import { IconClose } from './icons';
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/** The same explanation is available to mouse, keyboard and touch users. */
export function InfoTip({ label, title, subtitle, children }: { label: string; title?: string; subtitle?: string; children: ReactNode }) {
  const id = useId();
  const anchor = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const pinned = useRef(false);
  const suppressFocus = useRef(false);
  const hideTimer = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ left: 12, top: 12, width: 280, maxHeight: 400 });
  const cancelHide = () => {
    if (hideTimer.current !== null) window.clearTimeout(hideTimer.current);
    hideTimer.current = null;
  };
  const close = () => { cancelHide(); pinned.current = false; setOpen(false); };
  const leave = () => {
    if (!pinned.current) hideTimer.current = window.setTimeout(() => setOpen(false), 180);
  };

  useEffect(() => () => { if (hideTimer.current !== null) window.clearTimeout(hideTimer.current); }, []);
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const a = anchor.current?.getBoundingClientRect();
      const tip = panel.current;
      if (!a || !tip) return;
      const vv = window.visualViewport;
      const x = vv?.offsetLeft ?? 0, y = vv?.offsetTop ?? 0;
      const w = vv?.width ?? document.documentElement.clientWidth;
      const h = vv?.height ?? window.innerHeight;
      const width = Math.min(390, Math.max(1, w - 24));
      const above = Math.max(0, a.top - y - 20), belowSpace = Math.max(0, y + h - a.bottom - 20);
      const maxHeight = Math.max(1, Math.min(h - 24, Math.max(above, belowSpace)));
      const height = Math.min(tip.scrollHeight, maxHeight);
      const left = Math.max(x + 12, Math.min(a.right - width, x + w - width - 12));
      const below = a.bottom + 8;
      const top = Math.max(y + 12, Math.min(height <= belowSpace || belowSpace >= above ? below : a.top - height - 8, y + h - height - 12));
      setPosition({ left, top, width, maxHeight });
    };
    place();
    const observer = new ResizeObserver(place);
    if (panel.current) observer.observe(panel.current);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    window.visualViewport?.addEventListener('resize', place);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      window.visualViewport?.removeEventListener('resize', place);
    };
  }, [open, children]);
  useEffect(() => {
    if (!open) return;
    const outside = (e: PointerEvent) => {
      if (!anchor.current?.contains(e.target as Node) && !panel.current?.contains(e.target as Node)) close();
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault(); e.stopPropagation(); close();
      suppressFocus.current = true;
      anchor.current?.focus({ preventScroll: true });
      queueMicrotask(() => { suppressFocus.current = false; });
    };
    document.addEventListener('pointerdown', outside, true);
    document.addEventListener('keydown', escape, true);
    return () => {
      document.removeEventListener('pointerdown', outside, true);
      document.removeEventListener('keydown', escape, true);
    };
  }, [open]);

  return <>
    <button ref={anchor} type="button" className="rk-info-tip-trigger"
      aria-label={label} aria-expanded={open} aria-describedby={open ? id : undefined}
      data-d="пояснение-показателя"
      onPointerEnter={e => { if (e.pointerType !== 'touch') { cancelHide(); setOpen(true); } }}
      onPointerLeave={leave}
      onFocus={e => { if (!suppressFocus.current && e.currentTarget.matches(':focus-visible')) setOpen(true); }}
      onBlur={e => { if (!panel.current?.contains(e.relatedTarget as Node)) close(); }}
      onClick={e => { e.preventDefault(); e.stopPropagation(); cancelHide(); pinned.current = !pinned.current; setOpen(pinned.current); }}>
      <span aria-hidden="true">i</span>
    </button>
    {open && createPortal(<div data-kab style={{ display: 'contents' }}>
      <div ref={panel} id={id} role="tooltip" className="rk-info-tip" style={position}
        onPointerEnter={cancelHide} onPointerLeave={leave} onClick={e => e.stopPropagation()}>
        <header className="rk-info-tip-head">
          <span className="rk-info-tip-title">{title ?? label}</span>
          <button type="button" className="rk-info-tip-close" aria-label="Закрыть пояснение" onClick={close}><IconClose size={16}/></button>
        </header>
        {subtitle && <div className="rk-info-tip-subtitle">{subtitle}</div>}
        <div className="rk-info-tip-body">{children}</div>
      </div>
    </div>, document.body)}
  </>;
}
